import {
  json,
  type MetaFunction,
  type ActionFunctionArgs,
  redirect,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { GeneralErrorBoundary } from '~/components/error-boundary.tsx'
import { Spacer } from '~/components/ui/spacer.tsx'
import {
  authenticator,
  login,
  requireAnonymous,
} from '~/features/auth/auth.server.ts'
import { commitSession, getSession } from '~/utils/config/session.server.ts'
import { AuthLayout } from '~/components/layout/auth-layout.tsx'
import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { emailSchema, passwordSchema } from '~/utils/user-validation.ts'
import { z } from 'zod'
import { checkboxSchema } from '~/utils/zod-extensions.ts'
import { combineResponseInits, useIsPending } from '~/utils/misc.tsx'
import { safeRedirect } from 'remix-utils/safe-redirect'
import {
  CheckboxField,
  ErrorList,
  Field,
  PasswordField,
} from '~/components/ui/inputs/forms.tsx'
import { Label } from '~/components/ui/inputs/label.tsx'
import { StatusButton } from '~/components/ui/inputs/button/status-button.tsx'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'

export const meta: MetaFunction = () => {
  return [{ title: 'Login to ITraack' }]
}

export async function action({ request }: ActionFunctionArgs) {
  await requireAnonymous(request)
  const formData = await request.formData()
  // checkHoneypot(formData)
  const submission = await parseWithZod(formData, {
    schema: intent =>
      LoginFormSchema.transform(async (data, ctx) => {
        if (intent !== null) return { ...data, session: null }

        const session = await login(data)
        if ('errorMessage' in session) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: session.errorMessage,
          })
          return z.NEVER
        }

        return { ...data, session }
      }),
    async: true,
  })

  if (submission.status !== 'success' || !submission.value.session) {
    return json(
      { result: submission.reply({ hideFields: ['password'] }) },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { session, remember, redirectTo } = submission.value

  return handleNewSession({
    request,
    session,
    remember: remember ?? false,
    redirectTo,
  })
}

export async function handleNewSession(
  {
    request,
    session,
    redirectTo,
    remember,
  }: {
    request: Request
    session: { userId: string; id: number; expirationDate: Date }
    remember: boolean
    redirectTo?: string
  },
  responseInit?: ResponseInit,
) {
  const authSession = await getSession(request.headers.get('cookie'))
  const keyToSet = authenticator.sessionKey
  authSession.set(keyToSet, session.id)

  const headers = new Headers()
  headers.append(
    'Set-Cookie',
    await commitSession(authSession, {
      // Cookies with no expiration are cleared when the tab/window closes
      expires: remember ? session.expirationDate : undefined,
    }),
  )
  return redirect(
    safeRedirect(redirectTo),
    combineResponseInits(
      {
        headers,
      },
      responseInit,
    ),
  )
}

const LoginFormSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  redirectTo: z.string().optional(),
  remember: checkboxSchema(),
})

export default function LoginPage() {
  const actionData = useActionData<typeof action>()
  const isPending = useIsPending()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo')

  const [form, fields] = useForm({
    id: 'login-form',
    constraint: getZodConstraint(LoginFormSchema),
    defaultValue: { redirectTo },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LoginFormSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <AuthLayout>
      <h2 className="font-lato block text-center text-2xl font-bold text-primary lg:text-3xl">
        Acceso al Panel
      </h2>
      <Spacer size="4xs" />
      <div className="mx-auto w-full max-w-md text-primary">
        <Form method="POST" {...getFormProps(form)}>
          {/* <AuthenticityTokenInput />
          <HoneypotInputs /> */}
          <Field
            labelProps={{ children: 'Dirección de Correo Electrónico' }}
            inputProps={{
              ...getInputProps(fields.email, { type: 'email' }),
              autoComplete: 'email',
              autoFocus: true,
              placeholder: '',
            }}
            errors={fields.email.errors}
            className="text-center [&>input]:rounded-full [&>input]:border-2 [&>input]:border-dashed [&>input]:border-primary [&>input]:bg-white [&>input]:text-center"
          />
          <Spacer size="4xs" />
          <PasswordField
            labelProps={{
              asChild: true,
              children: (
                <div className="flex items-center justify-center text-center">
                  <Label htmlFor={fields.password.id}>Contraseña</Label>
                </div>
              ),
            }}
            inputProps={{
              ...getInputProps(fields.password, {
                type: 'password',
              }),
              placeholder: '',
              autoComplete: 'current-password',
            }}
            errors={fields.password.errors}
            className="text-center [&>input]:rounded-full [&>input]:border-2 [&>input]:border-dashed [&>input]:border-primary [&>input]:bg-white [&>input]:text-center"
          />
          <Spacer size="4xs" />
          <div className="[&>div>div]:font-primary flex justify-center gap-4 text-center [&>div>div]:flex [&>div>div]:items-center">
            <CheckboxField
              labelProps={{
                htmlFor: fields.remember.id,
                children: 'Mantenerme conectado',
              }}
              checkboxProps={getInputProps(fields.remember, {
                type: 'checkbox',
              })}
              errors={fields.remember.errors}
            />
          </div>

          <input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />

          <div className="text-center">
            <ErrorList errors={form.errors} id={form.errorId} />
          </div>

          <div className="mb-4 grid grid-cols-2 gap-4 pt-3">
            <StatusButton
              className="text-md w-full rounded-full bg-secondary font-bold text-primary  hover:bg-gray-200 hover:text-primary"
              status={isPending ? 'pending' : form.status ?? 'idle'}
              type="submit"
              disabled={isPending}
            >
              Acceder
            </StatusButton>
            <Link
              to={`/signup?${searchParams}`}
              className="text-md inline-flex w-full items-center justify-center rounded-full border-2 border-primary font-bold text-primary  hover:bg-gray-200 hover:text-primary"
            >
              Registro
            </Link>
          </div>

          <div className="text-body-xs mb-2 flex justify-center gap-1 text-center">
            <span>¿Has olvidado tu contraseña?</span>
            <Link
              to={`/forgot-password`}
              className="text-body-xs font-bold text-primary"
            >
              Click Aquí
            </Link>
          </div>
        </Form>
      </div>
    </AuthLayout>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
